/* eslint-disable react-hooks/exhaustive-deps */
import ShopFnbFacilityCarouselEmpty from '@/components/common/shop-fnb-facility/carousel-empty';
import ShopFnbFacilityHeader from '@/components/common/shop-fnb-facility/header';
import VirtualizedFacilityCarousel from '@/components/feature/facility/facility-carousel/VirtualizedFacilityCarousel';
import { FacilityPoiDetailModal } from '@/components/feature/facility/facility-poi-detail-modal';
import FacilitySearch from '@/components/feature/facility/facility-search';
import { BuildingMenuType } from '@/enums';
import { FloorGuideType } from '@/enums/floor-guide';
import { useOrientation } from '@/hooks/useOrientation';
import { PATHS } from '@/router/paths';
import { useConfigStore, useFacilityStore } from '@/stores';
import { useFloorGuideStore } from '@/stores/floorGuideStore';
import type { FacilityPoiDetailProps } from '@/types/facility';
import type { PoiContentSlide } from '@/types/shop';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styles from './FacilityPage.module.scss';
import { TrackingService } from '@/services/analytics/TrackingService';

const FacilityPage = () => {
    const ui = useConfigStore((state) => state.config.ui);
    const server = useConfigStore((state) => state.config.server);

    const { isHorizontal } = useOrientation();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setSelectedPoi, setFloorGuideType } = useFloorGuideStore();

    const rawFacilityContents = useFacilityStore((state) => state.rawFacilityContents);
    const facilityContents = useFacilityStore((state) => state.facilityContents);
    const currentMode = useFacilityStore((state) => state.currentMode);
    const initialize = useFacilityStore((state) => state.initialize);
    const setTranslationFunction = useFacilityStore((state) => state.setTranslationFunction);
    const resetFilters = useFacilityStore((state) => state.resetFilters);
    const updateFacilityContents = useFacilityStore((state) => state.updateFacilityContents);

    const [isOpenPoiModal, setIsOpenPoiModal] = useState(false);
    const [poiContent, setPoiContent] = useState<PoiContentSlide | null>(null);

    const isInitializedRef = useRef(false);
    const prevLanguageRef = useRef(ui.language);

    useEffect(() => {
        if (!isInitializedRef.current) {
            resetFilters();
            initialize(t);
            isInitializedRef.current = true;
        }
    }, [t, server?.store.buildings]);

    useEffect(() => {
        if (prevLanguageRef.current !== ui.language) {
            setTranslationFunction(t);
            prevLanguageRef.current = ui.language;
        }
    }, [t, ui.language]);

    useEffect(() => {
        if (rawFacilityContents.length > 0) {
            updateFacilityContents(currentMode);
        }
    }, [rawFacilityContents, currentMode]);

    const pageClassname = useMemo(() => {
        return clsx(styles['facility'], {
            [styles['facility__vertical']]: !isHorizontal,
            [styles['facility__horizontal']]: isHorizontal,
            [styles['facility__contrast']]: ui.contrast,
            [styles['facility__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['facility__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
        });
    }, [ui.screenMode, ui.contrast, isHorizontal]);

    const carouselClassname = useMemo(() => {
        return clsx(styles['facility'], {
            [styles['facility__carousel__horizontal']]: isHorizontal,
            [styles['facility__carousel__vertical']]: !isHorizontal,
            [styles['facility__carousel__contrast']]: ui.contrast,
            [styles['facility__carousel__horizontal__halfmode']]: ui.screenMode === 'split' && isHorizontal,
            [styles['facility__carousel__vertical__halfmode']]: ui.screenMode === 'split' && !isHorizontal,
            [styles['facility__carousel__vertical__empty']]: facilityContents.length === 0 && !isHorizontal,
            [styles['facility__carousel__vertical__empty__halfmode']]: facilityContents.length === 0 && !isHorizontal && ui.screenMode === 'split',
            [styles['facility__carousel__horizontal__empty']]: facilityContents.length === 0 && isHorizontal,
            [styles['facility__carousel__horizontal__empty__halfmode']]: facilityContents.length === 0 && isHorizontal && ui.screenMode === 'split',
        });
    }, [isHorizontal, ui.screenMode, ui.contrast, facilityContents.length]);

    const currentMenu = useMemo(() => {
        const listMenus = server?.buildingMenus?.[ui.language] || [];
        return listMenus.find((menu) => menu.menuType === BuildingMenuType.FACILITIES);
    }, [server?.buildingMenus, ui.language]);

    const onClickPoiContent = useCallback((data: PoiContentSlide) => {
        setPoiContent(data);
        setIsOpenPoiModal(true);
        TrackingService.trackPoi(data.data.id);
    }, []);

    const handleLocationCheck = () => {
        const buildingMapId = poiContent?.data?.studioBuildingId + '_' + poiContent?.data?.contents?.cstrCd['ko'];
        setSelectedPoi({ studioFloorID: poiContent?.data.studioFloorId, studioPoiId: poiContent?.data.studioPoiId, buildingId: buildingMapId });
        setFloorGuideType(FloorGuideType.MARKER);
        navigate(PATHS.FLOOR_GUIDE.CURRENT_LOCATION(poiContent?.data.studioFloorId || '', poiContent?.data.studioPoiId || ''));
    };

    const handleDirectionGuide = () => {
        const buildingMapId = poiContent?.data?.studioBuildingId + '_' + poiContent?.data?.contents?.cstrCd['ko'];
        setSelectedPoi({ studioFloorID: poiContent?.data.studioFloorId, studioPoiId: poiContent?.data.studioPoiId, buildingId: buildingMapId });
        setFloorGuideType(FloorGuideType.DIRECTION);
        navigate(PATHS.FLOOR_GUIDE.DIRECTION_GUIDE(poiContent?.data.studioFloorId || '', poiContent?.data.studioPoiId || ''));
    };

    const poiDetailProps = useMemo((): FacilityPoiDetailProps => {
        const formatPoiContent = {
            poiName: poiContent?.data.name[ui.language] ? poiContent?.data.name[ui.language] : poiContent?.data.name['ko'],
            poiImage: poiContent?.data.contents.shopTmrkImgUrlApp[ui.language]
                ? poiContent?.data.contents.shopTmrkImgUrlApp[ui.language]
                : poiContent?.data.contents.shopTmrkImgUrlApp['ko'],
            phoneNumber: poiContent?.data.contents.shopCntc[ui.language]
                ? poiContent?.data.contents.shopCntc[ui.language]
                : poiContent?.data.contents.shopCntc['ko'],
            description: poiContent?.data.contents.shopIntrdn[ui.language]
                ? poiContent?.data.contents.shopIntrdn[ui.language]
                : poiContent?.data.contents.shopIntrdn['ko'],
        };

        return {
            poiName: formatPoiContent.poiName || '',
            poiImage: formatPoiContent.poiImage || '',
            phoneNumber: formatPoiContent.phoneNumber || '',
            buildingAddress: poiContent?.buildingName || '',
            showBuildingAddress: server?.store?.buildings && server?.store.buildings?.length > 1,
            floorName: poiContent?.floor || '',
            description: formatPoiContent.description || '',
        };
    }, [poiContent?.id, ui.language]);

    return (
        <div className={pageClassname}>
            <div
                className={clsx(styles['facility__header'], {
                    [styles['facility__header--horizontal']]: isHorizontal,
                    [styles['facility__header--vertical']]: !isHorizontal,
                    [styles['facility__header--vertical__halfmode']]: !isHorizontal && ui.screenMode === 'split',
                })}
            >
                <ShopFnbFacilityHeader title={currentMenu?.name || ''} description={currentMenu?.description || ''} isHorizontal={isHorizontal} tabIndex={1} />
            </div>
            <div className={carouselClassname}>
                {facilityContents.length > 0 ? (
                    <VirtualizedFacilityCarousel contents={facilityContents} onClickPoiContent={onClickPoiContent} />
                ) : (
                    <ShopFnbFacilityCarouselEmpty
                        id="facility-empty-result"
                        contrast={ui.contrast}
                        halfMode={ui.screenMode === 'split'}
                        isHorizontal={isHorizontal}
                        tabIndex={2}
                    />
                )}
            </div>
            <div
                className={clsx(styles['facility__search'], {
                    [styles['facility__search--horizontal']]: isHorizontal,
                    [styles['facility__search--vertical']]: !isHorizontal,
                })}
            >
                <FacilitySearch />
            </div>
            <FacilityPoiDetailModal
                isOpen={isOpenPoiModal}
                poiDetail={poiDetailProps}
                onClose={() => setIsOpenPoiModal(false)}
                onLocationConfirmation={handleLocationCheck}
                onDirectionGuide={handleDirectionGuide}
            />
        </div>
    );
};

export default FacilityPage;
